import {
    useState,
    useCallback, useEffect,
} from 'react'
import {
    DateRangePicker, LocalizationProvider,
} from '@mui/x-date-pickers-pro'
import {
    AdapterMoment,
} from '@mui/x-date-pickers-pro/AdapterMoment'
import {
    DateRange,
} from '@mui/x-date-pickers-pro/models'
import {
    Moment,
} from 'moment/moment'
import clsx from 'clsx'

import {
    dateToDateStr,
    strToMoment,
    dateMask,
} from 'app/utils/date'
import useLabel from 'app/hooks/useLabel'

import useStyles from './DateRangeSelectorMui.styles'
import {
    PropsFromConfig,
} from './DateRangeSelectorMui.types'

type Props = PropsFromConfig<string> & {
    title?: string,
    name: string,
    onChange: (value: string, event: any, aggregation?: any) => void,
    disabled?: boolean, // both fields disabled
    required?: boolean,
    value?: string,
    className?: string,
    values?: object,
}

const DateRangeSelectorMui = ({
    title = '',
    name = '',
    onChange,
    value,
    disabled = false,
    required = false,
    className = '',
    toField,
    values,
    disabledFromField = false,
}: Readonly<Props>) => {
    const {
        classes,
    } = useStyles()
    const l = useLabel()
    const [
        state,
        setState,
    ] = useState<DateRange<Moment>>([
        null,
        null,
    ])

    const handleOnChange = useCallback((newValue: DateRange<Moment>) => {
        setState(newValue)
        const newFrom = newValue[0] ? dateToDateStr(newValue[0]) : null
        const newTo = newValue[1] ? dateToDateStr(newValue[1]) : null

        if (onChange) {
            onChange(newFrom, {}, {
                [toField.name]: newTo,
            })
        }
    }, [
        onChange,
        toField.name,
    ])

    useEffect(() => {
        if (value) {
            const fromDate = strToMoment(value)
            const toDate = values ? strToMoment(values[toField.name]) : null

            setState([
                fromDate,
                toDate,
            ])
        }
    }, [
        toField.name,
        value,
        values,
    ])

    return (
        <div
            data-testid={`dateRangeSelectMui-${name}`}
            className={clsx(
                className,
                {
                    [classes.isRequired]: required,
                    [classes.disabled]: disabled,
                },
            )}
        >
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale="en-gb"
            >
                <DateRangePicker
                    className={clsx(
                        classes.input,
                    )}
                    value={state}
                    onChange={handleOnChange}
                    disabled={disabled}
                    format={dateMask}
                    slotProps={{
                        textField: ({
                            position,
                        }) => {
                            return {
                                label: position === 'start' ? title : l(toField.labelKey),
                                className: clsx({
                                    [classes.disabled]: (disabledFromField && position === 'start') || disabled,
                                }),
                                focused: true,
                                variant: 'standard',
                                fullWidth: true,
                                InputProps: {
                                    startAdornment: required
                                        && (<div className={classes.requiredMark} />),
                                    disabled: (disabledFromField && position === 'start') || disabled,
                                },
                            }
                        },
                        field: {
                            dateSeparator: '',
                            sx: {
                                '& > :not(style) ~ :not(style)': {
                                    marginLeft: '8px !important',
                                },
                            },
                        },
                        actionBar: {
                            actions: [
                                'cancel',
                                'accept',
                            ],
                        },
                        calendarHeader: {
                            className: classes.calendarHeader,
                        },
                    }}
                    closeOnSelect={false}
                    minDate={disabledFromField ? strToMoment(value) : undefined}
                    rangePosition={disabledFromField ? 'end' : undefined}
                    disableDragEditing
                />
            </LocalizationProvider>
        </div>
    )
}

export default DateRangeSelectorMui
